import React, { useEffect, useState } from "react";
import {
  FaChartLine,
  FaWpforms,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaUserCircle,
  FaSignOutAlt,
} from "react-icons/fa";
import PendingApprovals from "./PendingApprovals";
import Approved from "./Approved";
import Rejected from "./Rejected";
import Form from "./Form";
import PendingApprovalsTable from "./PendingApprovalsTable";
import ApprovedTable from "./ApprovedTable";
import RejectedTable from "./RejectedTable";
import RecentActivities from "./RecentActivity";
import { signOut } from "aws-amplify/auth";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import translations from "../../translation"; // Import translations
import Layout from "../../layout/Layout";

const Sidebar = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [cardsData, setCardsData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(null);
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [language, setLanguage] = useState("en"); // Language state

  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    fetchData();
    fetchActivities();
    if (user) {
      setUserName(user.fullname || "Unknown User");
      setUserRole(user.role || "User");
    }
  }, []);

  useEffect(() => {
    const pageTitleMap = {
      dashboard: translations[language]?.dashboard || "Dashboard",
      form: translations[language]?.createAnnouncement || "Create Announcement",
      "pending-approvals": translations[language]?.pendingApprovals || "Pending Approvals",
      approved: translations[language]?.approved || "Approved",
      rejected: translations[language]?.rejected || "Rejected",
    };

    document.title = pageTitleMap[activeTab] || "Sidebar Page";

    // Update the direction based on the selected language
    const isRTL = language === "ar";
    document.documentElement.dir = isRTL ? "rtl" : "ltr"; // Set the dir attribute on the HTML tag
  }, [activeTab, language]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("sender_id", user.id);
      const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/user/count`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        toastr.error("Failed to fetch data from the API");
        return;
      }

      const data = await response.json();
      setCardsData(data);
    } catch (error) {
      console.error("Error fetching data: ", error);
      toastr.error("An error occurred while fetching data");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const formData = new FormData();
      formData.append("sender_id", user.id);
      const response = await fetch(`${process.env.REACT_APP_CHECK_USER_API}/api/user/recent-activities`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      setActivities(data);
    } catch (error) {
      console.error("Error fetching recent activities:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      localStorage.removeItem("user");
      window.location.href = "/login";
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const translate = (key) => translations[language][key] || key;

  const handleLanguageChange = (e) => {
    setLanguage(e.target.value);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        return (
          <Layout title={"Dashboard"}>
            <div className="flex flex-col gap-8">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <PendingApprovals content={cardsData?.pending || 0} language={language} />
                <Approved content={cardsData?.approved || 0} language={language} />
                <Rejected content={cardsData?.rejected || 0} language={language} />
              </div>
              <div>
                <RecentActivities content={activities?.data} language={language} />
              </div>
            </div>
          </Layout>
        );
      case "form":
        return <Form language={language} />;
      case "pending-approvals":
        return <PendingApprovalsTable language={language} />;
      case "approved":
        return <ApprovedTable language={language} />;
      case "rejected":
        return <RejectedTable language={language} />;
      default:
        return <div className="text-center text-gray-700">{translate("selectTabMessage")}</div>;
    }
  };

  return (
    <div
      className={`flex ${language === "ar" ? "flex-row-reverse text-arabic" : "flex-row"}`}

    >
      {/* Sidebar */}
      <div
        className={`bg-white h-screen fixed w-[17rem] ${language === "ar" ? "right-0 text-arabic" : "left-0"} border-r border-gray-200 flex flex-col justify-between`}
      >
        <div>
          <div className="flex items-center p-4 border-b border-gray-200">
            <img
              className="h-28 w-70 mt-2"
              src="/images/alnaghi.png"
              alt="Logo"
            />
          </div>
          <nav className="border-t border-gray-200">
            <button
              onClick={() => setActiveTab("dashboard")}
              className={`w-full flex items-center px-4 py-3 ${activeTab === "dashboard" ? "text-gray-700 bg-gray-100 font-semibold" : "text-gray-700"
                } hover:bg-gray-200`}
            >
              <FaChartLine className="w-5 h-5 mr-3 text-gray-600" />
              {translate("dashboard")}
            </button>
            <button
              onClick={() => setActiveTab("form")}
              className={`w-full flex items-center px-4 py-3 ${activeTab === "form" ? "text-gray-900 bg-gray-100 font-semibold" : "text-gray-700"
                } hover:bg-gray-200`}
            >
              <FaWpforms className="w-5 h-5 mr-3 text-gray-700" />
              {translate("createAnnouncement")}
            </button>
            <button
              onClick={() => setActiveTab("pending-approvals")}
              className={`w-full flex items-center px-4 py-3 ${activeTab === "pending-approvals" ? "text-gray-900 bg-gray-100 font-semibold" : "text-gray-700"
                } hover:bg-gray-200`}
            >
              <FaClock className="w-5 h-5 mr-3 text-gray-600" />
              {translate("pendingApprovals")}
            </button>
            <button
              onClick={() => setActiveTab("approved")}
              className={`w-full flex items-center px-4 py-3 ${activeTab === "approved" ? "text-gray-900 bg-gray-100 font-semibold" : "text-gray-700"
                } hover:bg-gray-200`}
            >
              <FaCheckCircle className="w-5 h-5 mr-3 text-gray-600" />
              {translate("approved")}
            </button>
            <button
              onClick={() => setActiveTab("rejected")}
              className={`w-full flex items-center px-4 py-3 ${activeTab === "rejected" ? "text-gray-900 bg-gray-100 font-semibold" : "text-gray-700"
                } hover:bg-gray-200`}
            >
              <FaTimesCircle className="w-5 h-5 mr-3 text-gray-600" />
              {translate("rejected")}
            </button>
          </nav>
        </div>
        <div className="p-2 border-t border-gray-200">
          <select
            value={language}
            onChange={handleLanguageChange}
            className="w-full text-sm bg-gray-100 border border-gray-300 text-gray-700 py-2 px-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
          >
            <option value="en">English</option>
            <option value="ar">Arabic</option>
          </select>
          <div className="flex items-center">
            <div>
              <p className="text-lg font-medium text-gray-700 ml-2">{userName}</p>
              <p className="text-sm text-gray-500 ml-2">
                {translate("userRole")}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center rounded-sm bg-red-500 text-white px-4 py-3 text-gray-700 hover:bg-red-400 hover:text-red-600 transition"
          >
            <FaSignOutAlt className="w-5 h-5 mr-3" />
            {translate("logout")}
          </button>
        </div>
      </div>


      {/* Main Content */}
      <main className={`flex-grow ${language === "ar" ? "mr-64 text-arabic" : "ml-64"} max-w-8xl px-8 py-8`}>{renderContent()}</main>
    </div>
  );
};

export default Sidebar;
